import {PermissionKey} from "../enums/permissions";

type PermissionHierarchy = {
  wildcard: PermissionKey;
  parent?: PermissionKey;
  children: PermissionKey[];
};

const PERMISSION_HIERARCHIES: PermissionHierarchy[] = [
  {
    wildcard: PermissionKey.SPRING_ALL,
    children: [
      PermissionKey.SPRING_OPERATIONS,
      PermissionKey.SPRING_DEBT_SETTLEMENT,
      PermissionKey.SPRING_INVENTORY,
      PermissionKey.SPRING_COMPLIANCE,
      PermissionKey.SPRING_BANKRUPTCIES,
      PermissionKey.SPRING_LEGAL_COLLECTIONS,
      PermissionKey.SPRING_CALL_CENTER,
      PermissionKey.SPRING_TASKS,
      PermissionKey.SPRING_ADMIN,
      PermissionKey.SPRING_GR4VY,
      PermissionKey.SPRING_PARTNER_MANAGEMENT,
    ],
  },
  {
    wildcard: PermissionKey.ACCOUNT_ALL,
    children: [
      PermissionKey.ACCOUNT_VIEW_FULL_SSN,
      PermissionKey.ACCOUNT_ASSIGN_TO_REX,
      PermissionKey.ACCOUNT_EDIT_NAME_DOB,
      PermissionKey.ACCOUNT_DISPUTE_OPERATIONS,
      PermissionKey.ACCOUNT_PAYMENT_REVERSALS,
      PermissionKey.ACCOUNT_UNMASK_COMMUNICATIONS,
      PermissionKey.ACCOUNT_GENERATE_DOCUMENTS,
      PermissionKey.ACCOUNT_MANAGE_FLAGS,
      PermissionKey.ACCOUNT_DELETE_CREDIT_REPORT,
      PermissionKey.ACCOUNT_UPDATE_DISPOSITION,
      PermissionKey.ACCOUNT_OVERRIDE_DISABLED_COMMS,
    ],
  },
  {
    wildcard: PermissionKey.SPRING_ADMIN_ALL,
    parent: PermissionKey.SPRING_ADMIN,
    children: [
      PermissionKey.SPRING_ADMIN_ROLES,
      PermissionKey.SPRING_ADMIN_PEOPLE,
    ],
  },
  {
    wildcard: PermissionKey.SPRING_GR4VY_ALL,
    parent: PermissionKey.SPRING_GR4VY,
    children: [PermissionKey.SPRING_GR4VY_ROUTING_VALIDATION],
  },
  {
    wildcard: PermissionKey.SPRING_OPERATIONS_ALL,
    parent: PermissionKey.SPRING_OPERATIONS,
    children: [
      PermissionKey.SPRING_OPERATIONS_ACCOUNT_IMPORTS,
      PermissionKey.SPRING_OPERATIONS_ACCOUNT_MANAGEMENT,
      PermissionKey.SPRING_OPERATIONS_PAYMENTS,
      PermissionKey.SPRING_OPERATIONS_WASHING,
      PermissionKey.SPRING_OPERATIONS_USER_LEVEL_MEDIA,
      PermissionKey.SPRING_OPERATIONS_BATCH_LEVEL_MEDIA,
      PermissionKey.SPRING_OPERATIONS_PRICING_IMPORT,
      PermissionKey.SPRING_OPERATIONS_PRICING_BATCH,
      PermissionKey.SPRING_OPERATIONS_IN_HOUSE_HANDLING,
    ],
  },
  {
    wildcard: PermissionKey.SPRING_DEBT_SETTLEMENT_ALL,
    parent: PermissionKey.SPRING_DEBT_SETTLEMENT,
    children: [
      PermissionKey.SPRING_DEBT_SETTLEMENT_OVERVIEW,
      PermissionKey.SPRING_DEBT_SETTLEMENT_BULK_OPERATIONS,
      PermissionKey.SPRING_DEBT_SETTLEMENT_ANONYMOUS_COMMS,
      PermissionKey.SPRING_DEBT_SETTLEMENT_COMPANIES,
    ],
  },
  {
    wildcard: PermissionKey.SPRING_CALL_CENTER_ALL,
    parent: PermissionKey.SPRING_CALL_CENTER,
    children: [
      PermissionKey.SPRING_CALL_CENTER_CONVERSATIONS,
      PermissionKey.SPRING_CALL_CENTER_BLOCKED_NUMBERS,
      PermissionKey.SPRING_CALL_CENTER_REX_SKILLS,
    ],
  },
  {
    wildcard: PermissionKey.SPRING_LEGAL_COLLECTIONS_ALL,
    parent: PermissionKey.SPRING_LEGAL_COLLECTIONS,
    children: [PermissionKey.SPRING_LEGAL_COLLECTIONS_BULK_OPERATIONS],
  },
  {
    wildcard: PermissionKey.SPRING_COMPLIANCE_ALL,
    parent: PermissionKey.SPRING_COMPLIANCE,
    children: [
      PermissionKey.SPRING_COMPLIANCE_OVERVIEW,
      PermissionKey.SPRING_COMPLIANCE_DISPUTES,
      PermissionKey.SPRING_COMPLIANCE_DOCUMENT_EXPORT,
    ],
  },
];

const parentToWildcard = new Map<PermissionKey, PermissionKey>();
const parentToHierarchy = new Map<PermissionKey, PermissionHierarchy>();
const wildcardToHierarchy = new Map<PermissionKey, PermissionHierarchy>();

PERMISSION_HIERARCHIES.forEach((h) => {
  wildcardToHierarchy.set(h.wildcard, h);
  if (h.parent !== undefined) {
    parentToWildcard.set(h.parent, h.wildcard);
    parentToHierarchy.set(h.parent, h);
  }
});

const hierarchiesReversed = [...PERMISSION_HIERARCHIES].reverse();

const descendantsCache = new Map<PermissionKey, PermissionKey[]>();

const computeAllDescendants = (perm: PermissionKey): PermissionKey[] => {
  const cached = descendantsCache.get(perm);
  if (cached) {
    return cached;
  }

  const descendants: PermissionKey[] = [];
  const childHierarchy = parentToHierarchy.get(perm);

  if (childHierarchy) {
    childHierarchy.children.forEach((child) => {
      descendants.push(child);
      descendants.push(...computeAllDescendants(child));
    });
  }

  descendantsCache.set(perm, descendants);
  return descendants;
};

PERMISSION_HIERARCHIES.forEach((h) => {
  if (h.parent) {
    computeAllDescendants(h.parent);
  }
});

const childrenSets = new Map<PermissionKey, Set<PermissionKey>>(
  PERMISSION_HIERARCHIES.map((h) => [h.wildcard, new Set(h.children)])
);

export const compressPermissions = (permissions: PermissionKey[]): PermissionKey[] => {
  const compressed = new Set(permissions);
  let changed = true;

  while (changed) {
    changed = false;
    const snapshot = new Set(compressed);

    for (const hierarchy of hierarchiesReversed) {
      if (snapshot.has(hierarchy.wildcard)) {
        continue;
      }

      const hasParent = hierarchy.parent ? snapshot.has(hierarchy.parent) : true;

      if (!hasParent && hierarchy.parent) {
        continue;
      }

      const childrenSet = childrenSets.get(hierarchy.wildcard);
      if (!childrenSet) continue;

      const allChildrenPresent = Array.from(childrenSet).every((child) => {
        const wildcardVersion = parentToWildcard.get(child);

        if (wildcardVersion && snapshot.has(wildcardVersion)) {
          return true;
        }

        if (snapshot.has(child)) {
          const descendants = descendantsCache.get(child);
          if (descendants && descendants.length > 0) {
            return descendants.every((d) => {
              const dWildcard = parentToWildcard.get(d);
              return snapshot.has(d) || (dWildcard && snapshot.has(dWildcard));
            });
          }
          return true;
        }

        return false;
      });

      if (hasParent && allChildrenPresent) {
        compressed.add(hierarchy.wildcard);

        childrenSet.forEach((child) => {
          compressed.delete(child);
          const wildcardVersion = parentToWildcard.get(child);
          if (wildcardVersion) {
            compressed.delete(wildcardVersion);
          }
          const descendants = descendantsCache.get(child);
          if (descendants) {
            descendants.forEach((d) => compressed.delete(d));
          }
        });

        if (hierarchy.parent) {
          compressed.delete(hierarchy.parent);
        }

        changed = true;
      } else if (hierarchy.parent && snapshot.has(hierarchy.parent)) {
        compressed.delete(hierarchy.parent);
        changed = true;
      }
    }
  }

  return Array.from(compressed);
};

export const expandPermissions = (permissions: PermissionKey[]): PermissionKey[] => {
  if (permissions.includes(PermissionKey.ALL)) {
    const allPermissions = new Set<PermissionKey>();
    for (const hierarchy of PERMISSION_HIERARCHIES) {
      if (hierarchy.parent) {
        allPermissions.add(hierarchy.parent);
      }
      for (const child of hierarchy.children) {
        allPermissions.add(child);
      }
    }
    return Array.from(allPermissions);
  }

  const expanded = new Set<PermissionKey>();

  for (const permission of permissions) {
    const hierarchy = wildcardToHierarchy.get(permission);

    if (hierarchy) {
      if (hierarchy.parent) {
        expanded.add(hierarchy.parent);
      }

      for (const child of hierarchy.children) {
        expanded.add(child);
        const descendants = descendantsCache.get(child);
        if (descendants) {
          descendants.forEach((d) => expanded.add(d));
        }
      }
    } else {
      expanded.add(permission);
    }
  }

  return Array.from(expanded);
};

export const hasPermission = (userPermissions: PermissionKey[], requiredPermission: PermissionKey): boolean => {
  const permissionsSet = new Set(userPermissions);

  if (permissionsSet.has(PermissionKey.ALL)) {
    return true;
  }

  if (permissionsSet.has(requiredPermission)) {
    return true;
  }

  const requiredPermStr = String(requiredPermission);

  for (const userPerm of userPermissions) {
    const userPermStr = String(userPerm);
    if (userPermStr.endsWith(".*")) {
      const prefix = userPermStr.slice(0, -2);
      if (requiredPermStr === prefix || requiredPermStr.startsWith(prefix + ".")) {
        return true;
      }
    }
  }

  return false;
};
